/**
 * PictView-Retold-Setup
 *
 * The SHARED first-run view. When the server runs in appliance mode (FirstRunAdmin) and no account
 * exists yet, the base app renders this instead of the sign-in view (the manifest reports
 * SetupRequired). It creates the first system-administrator account via the auth client's setup()
 * (POST /1.0/Setup, then an automatic sign-in) and, on success, calls OnAuthenticated - the same
 * callback the sign-in view uses - so the shell takes over.
 *
 * OVERRIDE SURFACE - an app re-skins the process without re-rolling the flow, in four graduated ways:
 *
 *   1. Copy: options.Setup.Title / Subtitle / SubmitLabel / SuccessMessage.
 *   2. Fields: options.Setup.Fields = [ { Key, Label, Type, Placeholder, Autocomplete, Optional } ].
 *      Key is the POST body key (CustomerName / DisplayName / Email / LoginID / Password by default).
 *      Replacing the array changes which fields are collected and their labels.
 *   3. Fields template: options.Setup.FieldsTemplate = an HTML string used verbatim for the fields
 *      area instead of the generated markup. Any input carrying data-setup-key="<bodyKey>" is
 *      collected on submit, so a custom template reuses all the wiring and the submit logic.
 *   4. Interaction: options.Setup.TransformPayload(payload) -> payload lets an app adjust the body
 *      before it is posted. For total control an app can register its own view and point
 *      setupViewHash() at it (exactly like loginViewHash()).
 *
 * Content overrides (1-3, minus the function hook) can also arrive from the server manifest's Setup
 * block, so a manifest-only app with no client JavaScript still re-skins the screen.
 *
 * Theme: mirrors the sign-in view - every themed background is paired with a themed foreground, with
 * light fallbacks so it reads in an unthemed host.
 */

const libPictView = require('pict-view');

function _escapeHtml(pText)
{
	return String(pText == null ? '' : pText).replace(/[&<>"]/g, (pCharacter) =>
		({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[pCharacter]));
}

class PictViewRetoldSetup extends libPictView
{
	_authClient() { return this.pict.providers['Retold-AuthClient']; }

	// The effective setup config: the view defaults with the app's options.Setup overrides layered on
	// top. A supplied Fields array replaces the default set wholesale (that is the intent of overriding
	// it); every other key is an independent override.
	_setupConfig()
	{
		let tmpDefault = (PictViewRetoldSetup.default_configuration && PictViewRetoldSetup.default_configuration.Setup) || {};
		return Object.assign({}, tmpDefault, this.options.Setup || {});
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap && typeof this.pict.CSSMap.injectCSS === 'function') { this.pict.CSSMap.injectCSS(); }
		this._decorate();
		this._buildFields();
		this._wire();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	_decorate()
	{
		if (typeof document === 'undefined') { return; }
		let tmpSetup = this._setupConfig();
		let tmpTitle = document.getElementById('Retold-Setup-Title');
		if (tmpTitle) { tmpTitle.textContent = tmpSetup.Title || 'Set up this server'; }
		let tmpSubtitle = document.getElementById('Retold-Setup-Subtitle');
		if (tmpSubtitle) { tmpSubtitle.textContent = tmpSetup.Subtitle || ''; }
		let tmpSubmit = document.getElementById('Retold-Setup-Submit');
		if (tmpSubmit) { tmpSubmit.textContent = tmpSetup.SubmitLabel || 'Create administrator account'; }
	}

	// Populate the fields area from a custom FieldsTemplate when the app supplies one, else from the
	// Fields config. Either way the inputs carry data-setup-key so submit() collects them generically.
	_buildFields()
	{
		if (typeof document === 'undefined') { return; }
		let tmpContainer = document.getElementById('Retold-Setup-Fields');
		if (!tmpContainer) { return; }
		let tmpSetup = this._setupConfig();
		if (typeof tmpSetup.FieldsTemplate === 'string' && tmpSetup.FieldsTemplate.length)
		{
			tmpContainer.innerHTML = tmpSetup.FieldsTemplate;
			return;
		}
		let tmpFields = Array.isArray(tmpSetup.Fields) ? tmpSetup.Fields : [];
		tmpContainer.innerHTML = tmpFields.map((pField) =>
		{
			let tmpKey = _escapeHtml(pField.Key);
			let tmpType = _escapeHtml(pField.Type || 'text');
			let tmpAutocomplete = pField.Autocomplete ? (' autocomplete="' + _escapeHtml(pField.Autocomplete) + '"') : '';
			let tmpPlaceholder = pField.Placeholder ? (' placeholder="' + _escapeHtml(pField.Placeholder) + '"') : '';
			return '<label>' + _escapeHtml(pField.Label || pField.Key) +
				'<input data-setup-key="' + tmpKey + '" type="' + tmpType + '"' + tmpAutocomplete + tmpPlaceholder + '></label>';
		}).join('');
	}

	_wire()
	{
		if (typeof document === 'undefined') { return; }
		let tmpForm = document.getElementById('Retold-Setup-Form');
		if (tmpForm && tmpForm.getAttribute('data-retold-wired') !== '1')
		{
			tmpForm.setAttribute('data-retold-wired', '1');
			tmpForm.addEventListener('submit', (pEvent) => { if (pEvent && pEvent.preventDefault) { pEvent.preventDefault(); } this.submit(); });
		}
	}

	_setError(pMessage)
	{
		if (typeof document === 'undefined') { return; }
		let tmpError = document.getElementById('Retold-Setup-Error');
		if (tmpError) { tmpError.textContent = pMessage ? String(pMessage) : ''; }
	}

	/**
	 * Read the form, create the first-run administrator, and (on success) hand off to the shell.
	 * Exposed so a test (or an app) can drive it without synthesizing a DOM submit event.
	 */
	submit()
	{
		if (typeof document === 'undefined') { return Promise.resolve(null); }
		let tmpForm = document.getElementById('Retold-Setup-Form');
		if (!tmpForm) { return Promise.resolve(null); }
		let tmpSetup = this._setupConfig();

		// Collect every input the template exposes by its body key. This is the single contract a
		// custom FieldsTemplate has to honor: inputs carry data-setup-key="<bodyKey>".
		let tmpPayload = {};
		let tmpInputs = tmpForm.querySelectorAll('[data-setup-key]');
		for (let i = 0; i < tmpInputs.length; i++)
		{
			let tmpKey = tmpInputs[i].getAttribute('data-setup-key');
			if (tmpKey) { tmpPayload[tmpKey] = tmpInputs[i].value; }
		}

		this._setError('');
		let tmpMissing = (Array.isArray(tmpSetup.Fields) ? tmpSetup.Fields : [])
			.filter((pField) => !pField.Optional)
			.some((pField) => !String(tmpPayload[pField.Key] || '').trim());
		if (tmpMissing) { this._setError('Please fill in every required field.'); return Promise.resolve(null); }

		if (typeof tmpSetup.TransformPayload === 'function') { tmpPayload = tmpSetup.TransformPayload(tmpPayload) || tmpPayload; }

		let tmpAuthClient = this._authClient();
		if (!tmpAuthClient || typeof tmpAuthClient.setup !== 'function') { this._setError('Setup is not available.'); return Promise.resolve(null); }

		return tmpAuthClient.setup(tmpPayload).then((pResult) =>
		{
			if (pResult && pResult.Setup && pResult.LoggedIn)
			{
				if (typeof this.options.OnAuthenticated === 'function') { this.options.OnAuthenticated(pResult); }
				return pResult;
			}
			if (pResult && pResult.Setup)
			{
				// Admin created but the auto sign-in did not complete (e.g. two-step login): send them
				// to the normal login. The base app wires OnSetupCreated to flip to the sign-in view.
				if (typeof this.options.OnSetupCreated === 'function') { this.options.OnSetupCreated(pResult); }
				this._setError(tmpSetup.SuccessMessage || 'Administrator created. Please sign in.');
				return pResult;
			}
			this._setError((pResult && pResult.Error) || 'Could not complete setup.');
			return pResult;
		});
	}
}

module.exports = PictViewRetoldSetup;
module.exports.default_configuration =
{
	ViewIdentifier: 'Retold-Setup',
	DefaultRenderable: 'Retold-Setup',
	DefaultDestinationAddress: '#App-Container',
	AutoRender: false,
	// The default first-run copy and fields. Neutral wording ("Workspace name") so any kit app reads;
	// an app overrides via options.Setup (or the manifest Setup block). See the class header.
	Setup:
	{
		Title: 'Set up this server',
		Subtitle: 'First run: create the administrator account.',
		SubmitLabel: 'Create administrator account',
		SuccessMessage: 'Administrator created. Please sign in.',
		Fields:
		[
			{ Key: 'CustomerName', Label: 'Workspace name', Type: 'text' },
			{ Key: 'DisplayName', Label: 'Your name', Type: 'text', Autocomplete: 'name' },
			{ Key: 'Email', Label: 'Email (optional)', Type: 'email', Optional: true, Autocomplete: 'email' },
			{ Key: 'LoginID', Label: 'Administrator login', Type: 'text', Autocomplete: 'username' },
			{ Key: 'Password', Label: 'Password', Type: 'password', Autocomplete: 'new-password' }
		]
	},
	CSS: /*css*/ `
		.retold-setup { display: flex; align-items: center; justify-content: center; height: 100%;
			background: var(--theme-color-background-primary, #f6f7f9);
			color: var(--theme-color-text-primary, #1f2430); }
		.retold-setup-form { display: flex; flex-direction: column; gap: 12px; min-width: 320px; max-width: 92vw; padding: 28px;
			border-radius: 10px; background: var(--theme-color-background-panel, #ffffff);
			color: var(--theme-color-text-primary, #1f2430);
			box-shadow: 0 2px 16px rgba(0,0,0,0.12); }
		.retold-setup-form h1 { font-size: 18px; margin: 0 0 2px 0; color: inherit; }
		.retold-setup-form label { display: flex; flex-direction: column; gap: 4px; font-size: 13px; color: inherit; }
		.retold-setup-form input { padding: 8px 10px; border-radius: 6px; font-size: 14px;
			background: var(--theme-color-background-primary, #ffffff);
			color: var(--theme-color-text-primary, #1f2430);
			border: 1px solid var(--theme-color-border-default, #d6d9de); }
		.retold-setup-form button { padding: 9px 12px; border: 0; border-radius: 6px; cursor: pointer; font-size: 14px;
			background: var(--theme-color-brand-primary, #3a6ea5); color: var(--theme-color-text-on-brand, #ffffff); }
		.retold-setup-hint { margin: 0 0 8px 0; font-size: 13px; color: var(--theme-color-text-secondary, #55606f); }
		.retold-setup-error { min-height: 18px; margin: 0; font-size: 13px;
			color: var(--theme-color-status-error, #c0392b); }
	`,
	Templates:
	[
		{
			Hash: 'Retold-Setup',
			Template: '<div class="retold-setup">' +
				'<form id="Retold-Setup-Form" class="retold-setup-form">' +
					'<h1 id="Retold-Setup-Title">Set up this server</h1>' +
					'<p id="Retold-Setup-Subtitle" class="retold-setup-hint"></p>' +
					'<div id="Retold-Setup-Fields"></div>' +
					'<button type="submit" id="Retold-Setup-Submit">Create administrator account</button>' +
					'<p id="Retold-Setup-Error" class="retold-setup-error"></p>' +
				'</form>' +
				'</div>'
		}
	],
	Renderables:
	[
		{ RenderableHash: 'Retold-Setup', TemplateHash: 'Retold-Setup', ContentDestinationAddress: '#App-Container' }
	]
};
